import { omit } from "lodash";

import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { sunoCommsApi } from "../api-base";
import {
  CallLocation,
  CallLocationFilter,
  CallLocationInput,
  PaginatedCallLocations
} from "../types/call-location.type";
import {
  LocationSettings,
  LocationSettingsFilter,
  LocationSettingsInput
} from "../types/location-settings.type";

export const locationsEndpoints = sunoCommsApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getLocations: builder.query<CallLocation[], CallLocationFilter>({
      query: (filter) =>
        `/locations/${formatValidQueryParamUrlCommaSeparated(filter, true)}`,
      providesTags: ["CallLocations"]
    }),
    // Paginated variant (ENG-1537): pass `paginated: true` + limit/skip to get
    // a `{ count, results }` envelope for the Locations settings grid.
    getLocationsPaginated: builder.query<
      PaginatedCallLocations,
      CallLocationFilter
    >({
      query: (filter) =>
        `/locations/${formatValidQueryParamUrlCommaSeparated(
          { ...filter, paginated: true },
          true
        )}`,
      providesTags: ["CallLocations"]
    }),
    getLocation: builder.query<CallLocation, string>({
      query: (id) => `/locations/${id}`,
      providesTags: ["CallLocations"]
    }),
    getLocationSettings: builder.query<
      LocationSettings,
      LocationSettingsFilter
    >({
      query: (filter) =>
        `/locationSettings/${formatValidQueryParamUrlCommaSeparated(
          filter,
          true
        )}`,
      providesTags: ["CallLocationSettings"]
    }),
    createLocation: builder.mutation<CallLocationInput, CallLocation>({
      query: (body) => ({
        url: `/locations/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["CallLocations"]
    }),
    updateLocation: builder.mutation<
      Partial<Omit<CallLocationInput, "id">> & Pick<CallLocationInput, "id">,
      Partial<Omit<CallLocation, "id">> & Pick<CallLocation, "id">
    >({
      query: (data) => ({
        url: `/locations/${data.id}`,
        method: "PATCH",
        body: omit(data, "id")
      }),
      invalidatesTags: ["CallLocations"]
    }),
    updateLocationSettings: builder.mutation<
      Partial<Omit<LocationSettingsInput, "id">> &
        Pick<LocationSettingsInput, "id">,
      LocationSettingsInput
    >({
      query: (data) => ({
        url: `/locationSettings/${data.id}`,
        method: "PATCH",
        body: omit(data, "id")
      }),
      invalidatesTags: ["CallLocationSettings"]
    })
  })
});

export const {
  useGetLocationsQuery,
  useGetLocationsPaginatedQuery,
  useGetLocationQuery,
  useGetLocationSettingsQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useUpdateLocationSettingsMutation
} = locationsEndpoints;
